/**
 * Push-to-talk: the transmit rules and the recogniser, driven as one thing.
 *
 * Holding the key is both an in-world action (you are keying a transmitter, and the rules in
 * `transmit.ts` say when you may) and a technical one (start the recogniser, collect a
 * transcript). Binding them here means no caller can start capture without passing the rules, or
 * pass the rules without capture actually starting.
 *
 * The whole thing resolves to a transcript or to nothing. It never throws at the UI and never
 * leaves the player mid-transmission: whichever ends first — the key coming up, the engine
 * settling, the duration cap — produces exactly one outcome.
 */
import type { SpeechError } from './stt.js';
import { type SpeechRecogniser, type SpeechResult } from './stt.js';
import { TransmitRules, type TransmitState } from './transmit.js';

export type TransmitOutcome =
  /** The player said something. */
  | { kind: 'transcript'; transcript: string; confidence: number }
  /** Keyed up, said nothing. Not an error — people do this. */
  | { kind: 'silent' }
  /** The rules would not allow it. */
  | { kind: 'refused'; state: TransmitState }
  /** Something went wrong with the microphone or engine. */
  | { kind: 'error'; error: SpeechError };

export interface PushToTalkEvents {
  /** Interim text, so the player can see the set is hearing them. */
  onPartial?: (text: string) => void;
  /** Called whenever the transmit state changes, for rendering. */
  onState?: (state: TransmitState) => void;
  /** Called when the duration cap cut the transmission short. */
  onCutOff?: () => void;
}

export interface PushToTalkOptions {
  recogniser: SpeechRecogniser;
  rules?: TransmitRules;
  events?: PushToTalkEvents;
  /** How often to check the duration cap while transmitting. */
  tickMs?: number;
}

export class PushToTalk {
  readonly rules: TransmitRules;

  private readonly recogniser: SpeechRecogniser;
  private readonly events: PushToTalkEvents;
  private readonly tickMs: number;

  private timer: ReturnType<typeof setInterval> | null = null;
  private pending: ((outcome: TransmitOutcome) => void) | null = null;
  private settled = true;

  constructor(options: PushToTalkOptions) {
    this.recogniser = options.recogniser;
    this.rules = options.rules ?? new TransmitRules();
    this.events = options.events ?? {};
    this.tickMs = options.tickMs ?? 200;
  }

  get available(): boolean {
    return this.recogniser.available;
  }

  get isTransmitting(): boolean {
    return this.rules.state().phase === 'transmitting';
  }

  state(): TransmitState {
    return this.rules.state();
  }

  /**
   * Key up. Resolves once the transmission is over, with whatever came of it.
   *
   * Note this resolves rather than rejecting on failure: a refused or broken transmission is a
   * normal branch for the caller (fall back to the authored options), not an exception.
   */
  press(): Promise<TransmitOutcome> {
    if (!this.settled) return Promise.resolve<TransmitOutcome>({ kind: 'silent' });

    if (!this.rules.begin()) {
      const outcome: TransmitOutcome = { kind: 'refused', state: this.rules.state() };
      this.events.onState?.(outcome.state);
      return Promise.resolve(outcome);
    }

    this.settled = false;
    this.events.onState?.(this.rules.state());

    return new Promise<TransmitOutcome>((resolve) => {
      this.pending = resolve;

      // The duration cap is enforced here, not by the engine: Web Speech has its own idea of when
      // an utterance ends, and the in-world limit has to hold regardless of what it decides.
      this.timer = setInterval(() => {
        if (!this.rules.hasExpired()) return;
        this.events.onCutOff?.();
        this.recogniser.stop();
      }, this.tickMs);

      void this.recogniser
        .start({
          onPartial: (result: SpeechResult) => this.events.onPartial?.(result.transcript),
          onFinal: (result: SpeechResult) => {
            this.finish(
              result.transcript
                ? {
                    kind: 'transcript',
                    transcript: result.transcript,
                    confidence: result.confidence,
                  }
                : { kind: 'silent' },
            );
          },
          onError: (error: SpeechError) => this.finish({ kind: 'error', error }),
        })
        .catch(() => {
          // `start` already reported through onError; this only stops the rejection escaping.
        });
    });
  }

  /** Key down — the player let go. The recogniser settles and `press()` resolves. */
  release(): void {
    if (this.settled) return;
    this.recogniser.stop();
  }

  /** Abandon the transmission entirely: no transcript, no outcome beyond `silent`. */
  cancel(): void {
    if (this.settled) return;
    this.recogniser.abort();
    this.finish({ kind: 'silent' });
  }

  /** Leaving the conversation — drop any cooldown, since it would be nonsense to carry it. */
  reset(): void {
    this.cancel();
    this.rules.reset();
    this.events.onState?.(this.rules.state());
  }

  dispose(): void {
    this.cancel();
    this.recogniser.dispose();
  }

  private finish(outcome: TransmitOutcome): void {
    if (this.settled) return;
    this.settled = true;

    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }

    // The rules are ended here rather than on key-up, so the cooldown starts when the
    // transmission actually finished — including when the engine kept listening past the release.
    const state = this.rules.end();
    this.events.onState?.(state);

    const resolve = this.pending;
    this.pending = null;
    resolve?.(outcome);
  }
}
